import { theme, type as text } from "../../theme";
import { PortalLink } from "../../components/PortalLink";
import { StatusTag, type StatusTone } from "../../components/Drawing";
import { tableHeadRow, fmtDay } from "./shared";

// Renewal readiness for one policy: GET /resilience/insurance/:policyId/readiness
// (backend/src/lib/insuranceReadiness.ts). The insurer questions are answered exactly as the
// assurance pack answers them; a statement the organisation made is shown beside the evidence and
// never counts towards readiness.

type ReadinessState = "from_records" | "attested" | "unknown";
type Answer = "yes" | "partial" | "no" | "not_evidenced";

export interface InsuranceReadinessView {
  policyId: string;
  currency: string;
  statementsFrom: { id: string; title: string } | null;
  exposureCoverage: { quantified: number; openRisks: number; priced: number };
  renewal: { expiresAt: string; daysToRenewal: number; stage: "expired" | "renew_now" | "prepare" | "in_force" };
  score: number;
  counts: { fromRecords: number; attested: number; unknown: number; favourable: number };
  questions: Array<{ key: string; question: string; state: ReadinessState; answer: Answer; evidence: string; statement: { answer: string; text: string } | null; href: string | null }>;
  warranties: Array<{ text: string; questionKeys: string[]; state: "evidenced" | "to_evidence" }>;
  cover: { priced: boolean; expectedAnnualLoss: number | null; oneInTwenty: number | null; coverLimit: number | null; excess: number | null; limitBelowOneInTwenty: boolean | null; note: string };
}

const STATE_LABEL: Record<ReadinessState, string> = { from_records: "From records", attested: "Your statement only", unknown: "Not evidenced" };
const ANSWER_LABEL: Record<Answer, string> = { yes: "Yes", partial: "Partly", no: "No", not_evidenced: "Not evidenced" };
const answerTone = (q: InsuranceReadinessView["questions"][number]): StatusTone =>
  q.state !== "from_records" ? "none"
    : q.answer === "yes" ? "met"
    : q.answer === "partial" ? "partial"
    : "gap";

const money = (v: number | null, ccy: string) => (v == null ? "—" : `${ccy} ${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`);

export function renewalText(r: InsuranceReadinessView["renewal"]): string {
  if (r.stage === "expired") return `Expired ${fmtDay(r.expiresAt)} (${-r.daysToRenewal} days ago)`;
  return `${r.daysToRenewal} day${r.daysToRenewal === 1 ? "" : "s"} to renewal (${fmtDay(r.expiresAt)})`;
}

export function InsuranceReadinessPanel({ view }: { view: InsuranceReadinessView }) {
  const r = view.renewal;
  const stageTone = r.stage === "expired" || r.stage === "renew_now" ? theme.color.dangerText : r.stage === "prepare" ? theme.color.warningText : theme.color.successText;
  const c = view.cover;
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "baseline" }}>
        <span style={{ ...text.sectionTitle, margin: 0, color: stageTone }}>{renewalText(r)}</span>
        <span style={text.body}><strong>{view.score}%</strong> ready from records</span>
        <span style={text.hint}>{view.counts.fromRecords} answered from records ({view.counts.favourable} favourable) · {view.counts.attested} on your statement only · {view.counts.unknown} not evidenced</span>
      </div>
      {view.statementsFrom
        ? <div style={text.hint}>Statements shown are from the assurance pack <PortalLink to="/assurance-sharing" style={{ color: "var(--c-accentText)" }}>{view.statementsFrom.title}</PortalLink>. They sit beside the evidence and never count as it.</div>
        : <div style={text.hint}>Add your own statements for the questions the records cannot answer in an <PortalLink to="/assurance-sharing" style={{ color: "var(--c-accentText)" }}>assurance pack</PortalLink> for this policy.</div>}

      <div style={{ overflowX: "auto", border: "1px solid var(--c-border)", borderRadius: 3 }}>
        <table style={{ ...text.hint, width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={tableHeadRow}>
            {["Insurer question", "Answer", "Source", "What answers it"].map((h) => <th key={h} style={{ padding: "7px 10px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {view.questions.map((q) => {
              const tone = answerTone(q);
              return (
                <tr key={q.key} style={{ borderBottom: "1px solid var(--c-border)", verticalAlign: "top" }}>
                  <td style={{ padding: "7px 10px", color: theme.color.text, maxWidth: 320 }}>{q.question}</td>
                  <td style={{ padding: "7px 10px" }}><StatusTag tone={tone}>{ANSWER_LABEL[q.answer]}</StatusTag></td>
                  <td style={{ padding: "7px 10px", color: q.state === "unknown" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{STATE_LABEL[q.state]}</td>
                  <td style={{ padding: "7px 10px", color: theme.color.muted }}>
                    {q.evidence}
                    {q.statement && <div style={{ marginTop: 4, fontStyle: "italic" }}>Your statement ({q.statement.answer}): {q.statement.text}</div>}
                    {q.href && <div><PortalLink to={q.href} style={{ color: "var(--c-accentText)", textDecoration: "none" }}>Open the record</PortalLink></div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <div style={{ ...text.sectionTitle, margin: 0, marginBottom: 6 }}>Warranties and conditions to evidence</div>
        {view.warranties.length === 0
          ? <div style={text.hint}>No warranties recorded on the policy. Copy them from the wording (one per line) so each can be evidenced before renewal.</div>
          : <ul style={{ ...text.hint, margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
            {view.warranties.map((w, i) => (
              <li key={i} style={{ color: theme.color.text }}>
                {w.text}{" "}
                <span style={{ fontWeight: 600, color: w.state === "evidenced" ? theme.color.successText : theme.color.warningText }}>
                  {w.state === "evidenced" ? "evidenced by the records" : w.questionKeys.length ? "not yet evidenced" : "no record on the platform answers this; keep your own evidence"}
                </span>
              </li>
            ))}
          </ul>}
      </div>

      <div style={{ padding: 12, borderRadius: 3, background: c.limitBelowOneInTwenty ? theme.color.dangerBg : theme.color.panelAlt, border: "1px solid var(--c-border)" }}>
        <div style={{ ...text.sectionTitle, margin: 0, marginBottom: 6 }}>Cover against modelled exposure</div>
        <div style={{ ...text.hint, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          {([
            ["Expected annual loss", money(c.expectedAnnualLoss, view.currency)],
            ["One-in-twenty-year loss", money(c.oneInTwenty, view.currency)],
            ["Cover limit", money(c.coverLimit, view.currency)],
            ["Excess", money(c.excess, view.currency)],
          ] as const).map(([k, v]) => (
            <div key={k}><div style={text.statLabel}>{k}</div><div style={{ ...text.itemTitle, fontVariantNumeric: "tabular-nums" }}>{v}</div></div>
          ))}
        </div>
        <div style={{ ...text.hint, marginTop: 8, color: c.limitBelowOneInTwenty ? theme.color.dangerText : theme.color.muted }}>
          {c.note} {view.exposureCoverage.priced < view.exposureCoverage.openRisks && `${view.exposureCoverage.priced} of ${view.exposureCoverage.openRisks} open risks are priced.`}{" "}
          <PortalLink to="/exposure" style={{ color: "var(--c-accentText)" }}>Open the exposure model</PortalLink>
        </div>
      </div>
    </div>
  );
}
